import * as RadixSelect from '@radix-ui/react-select'
import { type ComponentPropsWithoutRef, forwardRef } from 'react'
import { ChevronDownIcon } from '../../icons/ChevronDownIcon'
import { useUiLabels } from '../../labels'

export const Select = ({
  className,
  placeholder,
  options,
  value,
  label,
  onValueChange,
  disabled,
  id,
}: {
  className?: string
  placeholder?: string
  options: { value: string; label: string }[]
  value: string | undefined
  label?: string
  onValueChange: (value: string) => void
  disabled?: boolean
  id?: string
}) => {
  const labels = useUiLabels()
  const displayValue = typeof value === 'string' && options.some((option) => option.value === value) ? value : undefined

  return (
    <div className="ap:w-full ap:flex ap:flex-col ap:gap-xs">
      <RadixSelect.Root value={displayValue} onValueChange={onValueChange} disabled={disabled}>
        <RadixSelect.Group>
          <RadixSelect.Label className="ap:text-[14px] ap:font-semibold ap:text-content-primary">
            {label}
          </RadixSelect.Label>
        </RadixSelect.Group>
        <RadixSelect.Trigger
          id={id}
          data-dropdown
          className={`ap:group ap:flex ap:items-center ap:justify-between ap:gap-xl ap:w-full ap:px-xl ap:py-lg ap:text-base ap:leading-[1.2] ap:data-placeholder:text-content-disabled ap:bg-background ap:border ap:border-stroke-primary ap:rounded-md ap:cursor-pointer ap:focus:outline-none ap:disabled:bg-surface-accent ap:disabled:text-content-disabled ${className}`}
        >
          <div className=" ap:text-left ap:overflow-hidden ap:text-ellipsis ap:whitespace-nowrap ap:flex-1">
            <RadixSelect.Value placeholder={placeholder ?? labels.select} />
          </div>
          <RadixSelect.Icon className="ap:shrink-0 ap:text-content-secondary ap:ml-xl ap:transition-transform ap:group-data-[state=open]:rotate-180">
            <ChevronDownIcon />
          </RadixSelect.Icon>
        </RadixSelect.Trigger>
        <RadixSelect.Portal>
          <RadixSelect.Content
            data-dropdown
            position="popper"
            sideOffset={5}
            className="ap:bg-surface-background ap:text-base ap:text-content-secondary ap:border ap:border-stroke-primary ap:rounded-sm ap:shadow-soft ap:overflow-hidden ap:z-50 ap:opacity-100 ap:starting:ap:opacity-0 ap:transition-opacity ap:duration-150 ap:min-w-(--radix-select-trigger-width) ap:w-(--radix-select-trigger-width) ap:max-h-(--radix-select-content-available-height)"
          >
            <RadixSelect.Viewport className="ap:p-xs ap:overflow-y-auto ap:max-h-(--radix-select-content-available-height)">
              {options.length === 0 ? (
                <div className="ap:px-xl ap:py-md ap:text-content-secondary">{labels.noOptions}</div>
              ) : (
                options.map((option) => {
                  return (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  )
                })
              )}
            </RadixSelect.Viewport>
          </RadixSelect.Content>
        </RadixSelect.Portal>
      </RadixSelect.Root>
    </div>
  )
}

const SelectItem = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof RadixSelect.Item>>(
  ({ children, className, ...props }, forwardedRef) => {
    return (
      <RadixSelect.Item
        data-dropdown
        className={`ap:px-xl ap:py-md ap:cursor-pointer ap:hover:bg-surface-accent ap:hover:text-content-primary ap:hover:font-semibold ap:focus:bg-surface-accent ap:focus:text-content-primary ap:focus:font-semibold ap:outline-none ap:rounded ${className}`}
        {...props}
        ref={forwardedRef}
      >
        <RadixSelect.ItemText>{children}</RadixSelect.ItemText>
      </RadixSelect.Item>
    )
  },
)

SelectItem.displayName = 'SelectItem'
